import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { addDays, format } from 'date-fns'
import logoImage from 'src/assets/images/logo.png'
import { Spinner } from 'src/components/spinner'
import { PinEntryForm } from 'src/components/survey/pin-entry-form'
import { SurveyProvider, useSurveyForm } from 'src/hooks/survey/survey-provider'
import {
  GuestSurveyContext,
  completeGuestSurvey,
  getGuestSurveyContext,
  resumeGuestSurvey,
} from 'src/services/guest-survey'
import { SurveyForm } from 'src/pages/reservation-guest-form'
import { useSurveyPin } from 'src/stores/survey-pin'

/**
 * Standalone, chrome-free shell for the guest self-fill survey. No org
 * navigation, no other reservations — only the survey being filled.
 */
const GuestShell = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-screen w-full flex-col items-center bg-surface-hover">
    <header className="flex w-full justify-center border-b border-border bg-surface py-4 shadow-sm">
      <img src={logoImage} alt="RAPA" className="h-8" />
    </header>
    <main className="flex w-full max-w-3xl flex-1 flex-col px-4 py-8">{children}</main>
  </div>
)

const GuestMessage = ({ title, body }: { title: string; body: string }) => (
  <div className="mx-auto mt-12 flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-surface p-8 text-center shadow-sm">
    <h1 className="text-xl font-medium text-text-primary">{title}</h1>
    <p className="text-text-secondary">{body}</p>
  </div>
)

/**
 * Guest-facing counterpart of the staff SIRE retention banner. Informational
 * only — the guest has no action to take, so no urgency escalation.
 */
export const GuestRetentionNotice = ({
  context,
}: {
  context: Pick<GuestSurveyContext, 'checkoutDate' | 'reportsTra' | 'reportsSire'>
}) => {
  const { t } = useTranslation()
  if (!context.reportsTra && !context.reportsSire) return null
  const deletionDate = format(addDays(new Date(context.checkoutDate), 90), 'MMM d, yyyy')
  return (
    <div
      className="mb-4 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-700"
      data-testid="guest-retention-notice"
    >
      {t('guestSurvey.retentionNotice', { date: deletionDate })}
    </div>
  )
}

/**
 * Inner fill flow. Lives inside SurveyProvider so it can read the current
 * instance id and attach it to the token-scoped reservation on completion.
 */
const GuestSurveyFill = ({
  token,
  context,
  resumeInstanceId,
}: {
  token: string
  context: GuestSurveyContext
  resumeInstanceId?: string
}) => {
  const { t } = useTranslation()
  const { instance } = useSurveyForm()
  const instanceIdRef = useRef<string | null>(null)
  if (instance?.id) instanceIdRef.current = instance.id

  const {
    mutate: complete,
    isPending,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: () => completeGuestSurvey({ token, instanceId: instanceIdRef.current ?? '' }),
  })

  if (isSuccess) {
    const resumeUrl = `${window.location.origin}/guest-survey/${token}/${instanceIdRef.current}`
    return (
      <>
        <GuestMessage title={t('guestSurvey.completeTitle')} body={t('guestSurvey.completeBody')} />
        <div
          className="mx-auto mt-4 flex max-w-md flex-col gap-2 rounded-lg border border-border bg-surface p-4 text-sm shadow-sm"
          data-testid="guest-resume-link"
        >
          <p className="text-text-secondary">{t('guestSurvey.resumeLinkHint')}</p>
          <a href={resumeUrl} className="break-all text-primary underline">
            {resumeUrl}
          </a>
        </div>
      </>
    )
  }

  if (isPending) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  // A failed completion keeps the form so the guest can resubmit, with a clear
  // banner instead of the spinner silently dropping back to an unexplained form.
  return (
    <>
      <GuestRetentionNotice context={context} />
      {isError && (
        <div className="border-error/30 bg-error/10 mb-4 rounded-lg border p-3 text-center text-sm text-error">
          {t('guestSurvey.submitErrorBody')}
        </div>
      )}
      <SurveyForm onFinish={() => complete()} instanceId={resumeInstanceId} guestToken={token} />
    </>
  )
}

/**
 * Resume link: the guest enters their PIN to reopen their own instance. The PIN
 * is kept in memory only and sent as the `X-Survey-Pin` header.
 */
const GuestSurveyResume = ({
  token,
  instanceId,
  context,
}: {
  token: string
  instanceId: string
  context: GuestSurveyContext
}) => {
  const { t } = useTranslation()
  const pinFor = useSurveyPin((s) => s.instanceId)
  const setPin = useSurveyPin((s) => s.setPin)
  const [error, setError] = useState<string | null>(null)

  const { mutate: resume, isPending } = useMutation({
    mutationFn: (pin: string) => resumeGuestSurvey({ token, instanceId, pin }),
    onMutate: () => setError(null),
    onSuccess: (_data, pin) => setPin(instanceId, pin),
    onError: (err) => {
      const tooMany = isAxiosError(err) && err.response?.status === 429
      setError(t(tooMany ? 'guestSurvey.resumeLocked' : 'guestSurvey.resumeDenied'))
    },
  })

  if (pinFor !== instanceId) {
    return (
      <div className="mx-auto mt-12 w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-sm">
        <PinEntryForm onSubmit={resume} isPending={isPending} error={error} />
      </div>
    )
  }

  return (
    <SurveyProvider>
      <GuestSurveyFill token={token} context={context} resumeInstanceId={instanceId} />
    </SurveyProvider>
  )
}

export const GuestSurveyPage = () => {
  const { token, instanceId } = useParams()
  const { t } = useTranslation()

  const {
    data: context,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['guest-survey-context', token],
    queryFn: () => getGuestSurveyContext(token ?? ''),
    enabled: Boolean(token),
    retry: false,
  })

  const deadLink = useMemo(() => {
    if (!error) return false
    // Only the server's dead-link contract — 404 (revoked/malformed) or 410
    // (expired) — means the link itself is unusable. A transient 5xx/network
    // error is not a dead link, so it falls through to a retryable error state.
    return isAxiosError(error) && [404, 410].includes(error.response?.status ?? 0)
  }, [error])

  if (isLoading) {
    return (
      <GuestShell>
        <div className="flex flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </GuestShell>
    )
  }

  if (deadLink) {
    return (
      <GuestShell>
        <GuestMessage title={t('guestSurvey.invalidTitle')} body={t('guestSurvey.invalidBody')} />
      </GuestShell>
    )
  }

  // A non-dead-link error (5xx/network) or a missing context body is a
  // potentially transient failure — show a generic, reload-able message rather
  // than wrongly declaring the link dead.
  if (error || !context) {
    return (
      <GuestShell>
        <GuestMessage title={t('guestSurvey.errorTitle')} body={t('guestSurvey.errorBody')} />
      </GuestShell>
    )
  }

  return (
    <GuestShell>
      {instanceId ? (
        <GuestSurveyResume token={token ?? ''} instanceId={instanceId} context={context} />
      ) : (
        <SurveyProvider>
          <GuestSurveyFill token={token ?? ''} context={context} />
        </SurveyProvider>
      )}
    </GuestShell>
  )
}
